import Slide from "./slide";
import {PromoType, TrackingFeatureLabels} from "../types";
import {TrackingService} from "./tracking_service";
import {OcCinemaV1Events} from "@otto-ec/otto-components/cinema";

export default class ShopPromoLarge {
    private readonly trackingService: TrackingService;
    readonly slides: Array<Slide>
    private readonly promoType: PromoType;
    private readonly featureId: string;
    private readonly featureIndex: number;
    private readonly featureOrder: number;
    private readonly featureLabels: TrackingFeatureLabels;
    private readonly masFeatureTrackingLabels: TrackingFeatureLabels;
    private readonly masNextPiTrackingLabels: TrackingFeatureLabels;
    private readonly domElement: Element;

    constructor(shopPromoLargeDom: Element) {
        this.domElement = shopPromoLargeDom;
        this.promoType = this.getPromoType(shopPromoLargeDom);
        this.featureId = this.getFeatureId(shopPromoLargeDom);
        this.featureIndex = this.getFeatureIndex(shopPromoLargeDom);
        this.featureOrder = this.getFeatureOrder(shopPromoLargeDom);
        this.featureLabels = this.getFeatureLabels(shopPromoLargeDom);
        this.masFeatureTrackingLabels = this.getMasFeatureTrackingLabels(shopPromoLargeDom);
        this.masNextPiTrackingLabels = this.getMasNextPiTrackingLabels(shopPromoLargeDom);
        this.trackingService = new TrackingService()

        const slideContainers = Array.from(shopPromoLargeDom.querySelectorAll('.promo_shoppromo-large--slide'));
        this.slides = slideContainers.map((slideDom) => new Slide(slideDom))
        this.slides.forEach((slide) => {
            slide.registerClickEventHandler(() => this.trackClick(slide, this.slides.length))
        })

        this.trackInitialLoadedLargeShopPromo();
        this.registerScrollTrackingObserver();

        if (this.isCinemaEnabled(shopPromoLargeDom)) {
            const cinemaElement = shopPromoLargeDom.querySelector("oc-cinema-v1");
            cinemaElement!.addEventListener("oc-item-visibility-changed", (event: OcCinemaV1Events["oc-item-visibility-changed"]) => {
                if (event.detail.scrollDirection === "right") {
                    this.trackNextSlideLargeShopPromo(event.detail.visibleItems.indices, event.detail.stageIsScrolling);
                }
                else if (!event.detail.stageIsScrolling) {
                    this.trackPreviousSlideLargeShopPromo();
                }
            });
        }
    }

    trackedTilePositions: number[] = []

    registerScrollTrackingObserver(): void {
        /*                                     */
        const observer = new IntersectionObserver((entries) => {
            const slidesToTrack = this.getIntersectingSlides(entries);

            if (slidesToTrack.length > 0) {
                this.trackScroll(slidesToTrack);
                this.trackedTilePositions.push(...slidesToTrack.map(tile => tile.position));
            }

            slidesToTrack.forEach(slide => observer.unobserve(slide.slideDom));
        });

        this.slides.forEach((slide) => {
            observer.observe(slide.slideDom);
        })
    }

    getIntersectingSlides(entries: IntersectionObserverEntry[]): Slide[] {
        return entries
            .filter((entry) => entry.isIntersecting)
            .map((entry) => new Slide(entry.target as HTMLElement));
    }

    trackScroll(slidesToTrack: Slide[]): void {
        if (this.trackedTilePositions.length <= 0) {
            this.trackingService.sendMiniAction(this.promoType, this.featureId, slidesToTrack, "scroll");
        }
    }

    trackInitialLoadedLargeShopPromo(): void {
        if (!this.domElement.getAttribute("data-tracked")) {
            this.domElement.setAttribute("data-tracked", String(true));
            this.trackingService.sendLargeShopPromoInitialLoadedTrackingEvent(this.promoType, this.featureOrder, this.featureId, this.slides.length, this.slides, this.featureLabels, this.masFeatureTrackingLabels);
        }
    }

    trackNextSlideLargeShopPromo(currentSlidePositions: number[], stageIsScrolling: boolean): void {
        const slides: Slide[] = []
        currentSlidePositions.forEach(position => {
            if (this.slides[position]) {
                slides.push(this.slides[position]);
            }
        });
        this.trackingService.sendLargeShoppromoSlideNextTrackingEvent(this.promoType, this.featureOrder, this.featureId, this.slides.length, slides, this.featureLabels, this.masFeatureTrackingLabels, stageIsScrolling);
    }

    trackPreviousSlideLargeShopPromo(): void {
        this.trackingService.sendLargeShoppromoSlidePreviousTrackingEvent(this.promoType, this.featureOrder, this.featureId, this.slides.length, this.featureLabels, this.masFeatureTrackingLabels);
    }

    trackClick(slide: Slide, filledSlots: number) {
        this.trackingService.sendLargeShopPromoClickTrackingEvent(this.promoType, this.featureOrder, this.featureId, filledSlots, slide, this.featureLabels, this.masFeatureTrackingLabels, this.masNextPiTrackingLabels);
    }

    private getPromoType(container: Element): PromoType {
        return container.getAttribute('data-promo-type')! as PromoType;
    }

    private getFeatureOrder(container: Element): number {
        const dataFeatureOrder = container.closest('[data-feature-order]')?.getAttribute('data-feature-order');
        return dataFeatureOrder ? parseInt(dataFeatureOrder, 0) : -1;
    }

    private getMasFeatureTrackingLabels(container: Element): TrackingFeatureLabels {
        const dataFeatureTrackingLabels = container.closest('[data-feature-tracking-labels]')?.getAttribute("data-feature-tracking-labels");
        return dataFeatureTrackingLabels ? JSON.parse(dataFeatureTrackingLabels) : {};
    }

    private getMasNextPiTrackingLabels(container: Element): TrackingFeatureLabels {
        const dataMasNextPiTrackingLabels = container.closest('[data-next-pi-tracking-labels]')?.getAttribute("data-next-pi-tracking-labels");
        return dataMasNextPiTrackingLabels ? JSON.parse(dataMasNextPiTrackingLabels) : {};
    }

    private getFeatureId(container: Element): string {
        return container.getAttribute('data-feature-id')!;
    }

    private getFeatureIndex(container: Element): number {
        return parseInt(container.getAttribute('data-feature-index')!, 0);
    }

    private getFeatureLabels(container: Element): TrackingFeatureLabels {
        return JSON.parse(container.getAttribute('data-feature-labels')!);
    }

    private isCinemaEnabled(container: Element): boolean {
        return container.querySelector("oc-cinema-v1") != null
    }
}